import { fetchQuery } from 'react-relay'
import { Subject } from 'rxjs'
import Env from '../RelayEnvironment'
import { ConfigurationServiceGlobalSettingsQuery } from './__generated__/ConfigurationServiceGlobalSettingsQuery.graphql'
import { ConfigurationServiceGlobalSettingsUpdatedSubscription } from './__generated__/ConfigurationServiceGlobalSettingsUpdatedSubscription.graphql'
import { Currency, CurrencyEnum, CurrencyList } from './coins/coins.types'
import * as coins from './coins/coins.json'
import { BetLimitT, getParsedCoins } from './coins/coins'
import { settingsQuery } from './ConfigurationService.gql'
import { ConfigurationServiceGlobalSettingsUpdatedSubscription$data } from './__generated__/ConfigurationServiceGlobalSettingsUpdatedSubscription.graphql'

enum ConfigurationKey {
  BET_LIMIT = 'bet_limit',
  BET_LIMIT_MP = 'bet_limit_mp',
  CURRENCIES = 'currencies',
  GAMES_DISABLED = 'disabled',
  RAIN_LIMITS = 'rain_limits',
  TIP_LIMITS = 'tip_limits',
}
interface BetLimit {
  min: number
  max: number
}

type Config = {
  [key in keyof CurrencyEnum]: BetLimit | Currency
}

type BetLimitMap = Map<CurrencyEnum, BetLimit>

export const globalConfUpdated = new Subject<void>()

class ConfigurationService {
  public static instance = new ConfigurationService()
  private config: Map<ConfigurationKey, any> = new Map()
  currencies: CurrencyList
  externalCurrencies: CurrencyList
  bet_limit: BetLimitT

  private constructor() {
    const { all, external, bet_limit } = getParsedCoins({ currencies_list: coins })
    this.currencies = all
    this.externalCurrencies = external
    this.bet_limit = bet_limit
  }

  public async load(): Promise<void> {
    const defaultBets = this.bet_limit
    this.config = new Map<ConfigurationKey, Config>([
      [
        ConfigurationKey.BET_LIMIT,
        // Will be replaced on runtime
        defaultBets,
      ],
      [ConfigurationKey.CURRENCIES, this.currencies],
    ])
    return this.reload()
  }

  public reload() {
    return new Promise<void>((resolve, reject) => {
      fetchQuery<ConfigurationServiceGlobalSettingsQuery>(Env, settingsQuery, {}).subscribe({
        next: ({ globalSettings }) => {
          const limits = {}
          if (!globalSettings?.betLimits || !globalSettings.betLimitsMp) {
            return
          }
          globalSettings.betLimits.forEach((limit: { currency: string; limits: readonly number[] }) => {
            limits[limit.currency] = { min: limit.limits[0], max: limit.limits[1] }
          })
          const limitsMp = {}
          globalSettings.betLimitsMp.forEach((limit: { currency: string; limits: readonly number[] }) => {
            limitsMp[limit.currency] = { min: limit.limits[0], max: limit.limits[1] }
          })

          this.config.set(ConfigurationKey.BET_LIMIT_MP, limitsMp)
          this.config.set(ConfigurationKey.BET_LIMIT, limits)
          this.config.set(ConfigurationKey.GAMES_DISABLED, globalSettings.disabled)
          this.config.set(ConfigurationKey.RAIN_LIMITS, globalSettings.rainLimits)
          this.config.set(ConfigurationKey.TIP_LIMITS, globalSettings.tipLimits)

          globalConfUpdated.next()
          resolve()
        },
        error: () => {
          reject()
        },
      })
    })
  }

  public updateFromResponse({ globalSettingsUpdated }: ConfigurationServiceGlobalSettingsUpdatedSubscription['response']) {
    const globalSettings = globalSettingsUpdated
    const limits = {} as BetLimitMap
    if (!globalSettings?.betLimits || !globalSettings.betLimitsMp) {
      return
    }
    globalSettings.betLimits.forEach((limit: { currency: string; limits: readonly number[] }) => {
      limits[limit.currency] = { min: limit.limits[0], max: limit.limits[1] }
    })
    const limitsMp = {} as BetLimitMap
    globalSettings.betLimitsMp.forEach((limit: { currency: string; limits: readonly number[] }) => {
      limitsMp[limit.currency] = { min: limit.limits[0], max: limit.limits[1] }
    })

    this.config.set(ConfigurationKey.BET_LIMIT_MP, limitsMp)
    this.config.set(ConfigurationKey.BET_LIMIT, limits)
    this.config.set(ConfigurationKey.GAMES_DISABLED, globalSettings.disabled)
    globalConfUpdated.next()
  }

  public getCurrencies(): CurrencyList {
    return this.currencies
  }

  public isExternal({ key }): CurrencyList {
    return this.externalCurrencies[key] || false
  }

  public getCurrency(shortName: string): Currency {
    const key = Object.keys(this.currencies).find((x) => x.toLowerCase() === shortName.toLowerCase())
    if (key) {
      return this.currencies[key]
    }

    // Default to FUN
    return coins['FUN']
  }

  public getBTCLikeCurrencies(): string[] {
    return ['BTC', 'DASH', 'LTC', 'BCH']
  }

  public getTradeableCurrencies(): Partial<CurrencyList> {
    /**
     * Purpose of this function is to return a list of currencies that are "playable"
     * But we have semi playable currency FUN, in some places we would like to show it
     * it another places we don't want to see it at all, in the wallet for example.
     *
     * Currently we just remove FUN currency here, it's not optimal, but correct solution
     * requires a lot of changes in the codebase. When FUN is removed from here, it can
     * still be used but wont be visible unless the caller specifically asks for it.
     */
    const currencies = {
      ...this.currencies
    } as Partial<CurrencyList>

    delete currencies['FUN']
    return currencies
  }

  public getBetLimits(currency: Currency): { min: number; max: number } {
    if (currency === undefined) {
      return { min: 0, max: 0 }
    }
    const limits = this.config.get(ConfigurationKey.BET_LIMIT) as BetLimitMap
    if (currency.short === 'XXX') {
      return limits['FUN' as string]
    }
    if (currency.short && currency.short in limits) {
      return limits[currency.short]
    }
    return { min: 0, max: Number.MAX_VALUE }
  }

  public getBetLimitsMp(currency: Currency): { min: number; max: number } {
    if (currency === undefined) {
      return { min: 0, max: 0 }
    }
    const limits = this.config.get(ConfigurationKey.BET_LIMIT_MP) as BetLimitMap
    if (currency.short === 'XXX') {
      return limits['FUN' as string]
    }
    if (currency.short && currency.short in limits) {
      return limits[currency.short]
    }
    return { min: 0, max: Number.MAX_VALUE }
  }

  public areGamesDisabled(): boolean {
    return this.config.get(ConfigurationKey.GAMES_DISABLED)
  }

  public getRainLimitForCurrency(currency: string): [number, number] {
    const limits = this.config.get(ConfigurationKey.RAIN_LIMITS)
    return [limits['rain_min_' + currency], limits['rain_max_' + currency]]
  }

  public getTippingLimitsInUSD(): [number, number] {
    const limits = this.config.get(ConfigurationKey.TIP_LIMITS)
    return [limits.tipMin, limits.tipMax]
  }
}

export { ConfigurationKey }
export default ConfigurationService
